/* cz-cart-drawer.js
   Mini behaviors del cart drawer Cozzari.

   - <cz-cart-addon> toggle collapse (Phase 4: nota, Phase 5: sconto)
   - <cz-cart-discount> (Phase 5)
   - hook cz:cart:updated (Phase 9)
*/

class CzCartAddon extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-toggle]');
    this.body = this.querySelector('[data-body]');
    this.plusEl = this.querySelector('[data-toggle] .cz-cart-addon__plus');
    if (!this.toggleBtn || !this.body) return;
    this.toggleBtn.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const isOpen = this.dataset.open === 'true';
    if (isOpen) {
      this.dataset.open = 'false';
      if (this.plusEl) this.plusEl.textContent = '+';
    } else {
      this.dataset.open = 'true';
      if (this.plusEl) this.plusEl.textContent = '−';
      // Focus dopo che la transizione max-height ha aperto il body
      setTimeout(() => {
        const focusable = this.body.querySelector('input, textarea, button');
        if (focusable) focusable.focus();
      }, 250);
    }
  }
}

if (!customElements.get('cz-cart-addon')) {
  customElements.define('cz-cart-addon', CzCartAddon);
}

class CzCartDiscount extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-discount-input]');
    this.applyBtn = this.querySelector('[data-apply]');
    this.errorEl = this.querySelector('[data-error]');
    // I badge dei codici applicati vivono nel toggle header del addon (data-badges)
    const addon = this.closest('cz-cart-addon');
    this.badgesEl = addon?.querySelector('[data-badges]');

    this.applyBtn?.addEventListener('click', () => this.applyCode());
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.applyCode(); }
    });

    // Click sul × del badge → remove. Stoppa la propagation così non triggera il toggle.
    this.badgesEl?.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const code = removeBtn.closest('[data-code]')?.dataset.code;
        if (code) this.removeCode(code);
      }
    });
  }

  async applyCode() {
    const code = this.input.value.trim();
    if (!code) return;
    this.setLoading(true);
    this.clearError();

    try {
      const existing = Array.from(this.badgesEl?.querySelectorAll('[data-code]') || []).map(el => el.dataset.code);
      const newList = [...existing, code];
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: newList.join(',') }),
      });
      const data = await res.json();

      const acceptedCodes = (data.cart_level_discount_applications || []).map(d => d.title)
        .concat((data.items || []).flatMap(it =>
          (it.line_level_discount_allocations || [])
            .map(a => a.discount_application?.title)
            .filter(Boolean)
        ));

      if (!acceptedCodes.some(c => c?.toLowerCase() === code.toLowerCase())) {
        this.showError(`Il codice "${code}" non è valido o non applicabile.`);
        return;
      }

      this.input.value = '';
      this.addBadge(code);
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'discount-apply' } }));
    } catch (err) {
      this.showError("Errore di rete. Riprova.");
    } finally {
      this.setLoading(false);
    }
  }

  async removeCode(code) {
    const remaining = Array.from(this.badgesEl?.querySelectorAll('[data-code]') || [])
      .map(el => el.dataset.code)
      .filter(c => c.toLowerCase() !== code.toLowerCase());
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: remaining.join(',') }),
      });
      this.removeBadge(code);
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'discount-remove' } }));
    } catch (err) {
      this.showError("Errore di rete. Riprova.");
    }
  }

  addBadge(code) {
    if (!this.badgesEl) return;
    // Evita duplicati case-insensitive
    const exists = Array.from(this.badgesEl.querySelectorAll('[data-code]'))
      .some(el => el.dataset.code.toLowerCase() === code.toLowerCase());
    if (exists) return;
    const safeCode = this.escapeHTML(code);
    const li = document.createElement('li');
    li.className = 'cz-cart-discount__badge';
    li.dataset.code = code;
    li.innerHTML = `<span>${safeCode}</span><button type="button" class="cz-cart-discount__badge-remove" data-remove aria-label="Rimuovi ${safeCode}">×</button>`;
    this.badgesEl.appendChild(li);
  }

  removeBadge(code) {
    if (!this.badgesEl) return;
    const el = Array.from(this.badgesEl.querySelectorAll('[data-code]'))
      .find(li => li.dataset.code.toLowerCase() === code.toLowerCase());
    el?.remove();
  }

  escapeHTML(str) {
    return String(str || '').replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]));
  }

  setLoading(loading) {
    this.applyBtn.disabled = loading;
    this.applyBtn.textContent = loading ? '…' : 'Applica';
  }

  showError(msg) {
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  clearError() {
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }
}

if (!customElements.get('cz-cart-discount')) {
  customElements.define('cz-cart-discount', CzCartDiscount);
}

/* <cz-cart-addon-card> — card add-on (incisione / gift) toggleabile.
   Head sempre visibile (icona + titolo + tag + "+" oro). Click → si apre
   sotto: intro discorsiva + field configurati + CTA "Modifica X →".
   Stato collassato di default. */
class CzCartAddonCard extends HTMLElement {
  connectedCallback() {
    this.head = this.querySelector('[data-toggle]');
    this.editBtn = this.querySelector('[data-edit-engraving], [data-edit-gift]');

    this.head?.addEventListener('click', () => this.toggle());
    this.editBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openEditDrawer();
    });
  }

  toggle() {
    const open = this.dataset.open === 'true';
    this.dataset.open = open ? 'false' : 'true';
    this.head?.setAttribute('aria-expanded', String(!open));
  }

  openEditDrawer() {
    const kind = this.dataset.kind;
    if (kind === 'engraving') {
      document.dispatchEvent(new CustomEvent('cz:engraving-drawer:open', {
        detail: {
          mode: 'edit',
          lineItemKey: this.dataset.lineKey,
          parentKey: this.dataset.parentKey,
        }
      }));
    } else if (kind === 'gift') {
      document.dispatchEvent(new CustomEvent('cz:gift-drawer:open', {
        detail: {
          mode: 'edit',
          lineItemKey: this.dataset.lineKey,
          packageId: this.dataset.packageId,
        }
      }));
    }
  }
}

if (!customElements.get('cz-cart-addon-card')) {
  customElements.define('cz-cart-addon-card', CzCartAddonCard);
}

/* Intercept globale dei form add-to-cart che non sono dentro <product-form>
   Dawn (es. main-product-drawer.liquid usa solo {% form 'product' %} Liquid).
   Senza questo intercept, il submit fa POST classico /cart/add → Shopify
   redirect a /cart. Qui invece: AJAX POST + open cart-drawer. */
(function interceptAddToCartForms() {
  function isAddToCartForm(form) {
    if (!form) return false;
    if (form.closest('product-form')) return false; // Dawn lo gestisce già
    const action = (form.getAttribute('action') || '').toLowerCase();
    if (!action.includes('/cart/add')) return false;
    // Skip SOLO se CzAddonSync gestirà davvero il submit (incisione o pacchetto
    // attivati dall'utente). Se cz-product-addons è presente ma nessun addon è
    // attivo, CzAddonSync non interviene → tocca a noi intercettare, altrimenti
    // il form fa submit classico → /cart → redirect home (flash prima del drawer).
    const aside = form.closest('.product-drawer__main, .product-drawer__aside, aside');
    const addons = aside?.querySelector('cz-product-addons');
    if (addons) {
      const s = addons.state;
      const hasEngraving = s?.engraving?.active && s.engraving.text?.trim().length > 0;
      const hasGift = s?.gift?.active && s.gift.dedication?.trim().length > 0;
      if (hasEngraving || hasGift) return false; // CzAddonSync prende il controllo
    }
    return true;
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form');
    if (!isAddToCartForm(form)) return;
    e.preventDefault();

    const cartDrawer = document.querySelector('cart-drawer');
    const submitBtn = form.querySelector('[type="submit"][name="add"], [type="submit"]');
    const originalLabel = submitBtn?.querySelector('span')?.textContent;
    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
    }

    const formData = new FormData(form);
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      formData.append('sections', cartDrawer.getSectionsToRender().map((s) => s.id).join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();

      if (data.status) {
        console.warn('add-to-cart error', data);
        return;
      }

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data); // include this.open()
        // Dawn rimuove is-empty solo da .drawer__inner, ma nel nostro markup la
        // classe sta sull'outer <cart-drawer>. Senza questa sync, la regola CSS
        // cart-drawer.is-empty .drawer__inner { display:grid; place-items:center }
        // collassa visivamente il layout al primo add (items presenti ma invisibili).
        if (cartDrawer.classList.contains('is-empty')) {
          cartDrawer.classList.remove('is-empty');
        }
      } else if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
      document.dispatchEvent(new CustomEvent('cz:cart:updated', { detail: { source: 'pdp-add' } }));
    } catch (err) {
      console.error('add-to-cart fetch failed', err);
    } finally {
      if (submitBtn) {
        submitBtn.removeAttribute('aria-disabled');
        submitBtn.classList.remove('loading');
        const span = submitBtn.querySelector('span');
        if (span && originalLabel) span.textContent = originalLabel;
      }
    }
  }, true);
})();

// Apri il drawer carrello su hash #cart-drawer-open (usato dal redirect /cart → /).
(function openDrawerFromHash() {
  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      drawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
    }
    return true;
  }
  function maybeOpen() {
    if (window.location.hash !== '#cart-drawer-open') return;
    // Rimuovi hash subito (no scroll behaviour, no history clutter)
    if (history.replaceState) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    // Polling breve: cart-drawer custom element può non essere ancora upgradato
    let tries = 0;
    const tick = () => {
      if (openCartDrawer() || tries > 20) return;
      tries++;
      setTimeout(tick, 100);
    };
    tick();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeOpen);
  } else {
    maybeOpen();
  }
})();
